import { CatalogRendererProps } from '@copilotkit/a2ui-renderer';
import React, { lazy, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '~/components/Button';
import { GeoFilter } from '~/contexts/FilterContext';
import { PersonFilterInput } from '~/types/api.graphql';
import { createGeoFilterHelper, createGeoFilterHelperFromGraphQL } from '~/utils/filters';
import { MAX_GEO_FILTER_SUBMISSION_CHARS } from '~/utils/geoFilter';

import { CatalogText, ErrorLine } from '../visitiqCatalog.styles';
import { TriggerSubtitle } from './GeoFilterField.styles';

// Split point: the modal pulls in MapView and with it mapbox-gl / mapbox-gl-draw / turf. This
// renderer is reached statically from AppContainer (→ AIInsightsSidebar → A2uiSurface →
// visitiqCatalog), so a static import here would put all of that in the chunk every authenticated
// page loads — for users who never open a geo filter. The modal only renders once `open`, so
// deferring the import costs nothing at first paint.
const GeoFilterModal = lazy(() => import('./GeoFilterModal'));

interface CheckableProps {
  validationErrors?: string[];
  isValid?: boolean;
}

interface GeoFilterRendererProps extends CheckableProps {
  label?: string;
  value?: string;
  setValue: (value: string) => void;
}

const parseInitialGeoFilters = (value: string | undefined): GeoFilter[] => {
  if (!value) return [];
  try {
    const personFilters = JSON.parse(value) as PersonFilterInput[];
    return personFilters
      .filter((personFilter) => !!personFilter.geoFilter)
      .map((personFilter) => createGeoFilterHelperFromGraphQL(personFilter.geoFilter).fromGraphQLGeoFilter());
  } catch {
    return [];
  }
};

const VisitiqGeoFilter = ({ props }: CatalogRendererProps<GeoFilterRendererProps>): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);
  const selectedCount = useMemo(() => parseInitialGeoFilters(props.value).length, [props.value]);

  const handleConfirm = (geoFilters: GeoFilter[]) => {
    const personFilters = geoFilters.map((geoFilter) => createGeoFilterHelper(geoFilter).toGraphQLGeoFilter());
    if (process.env.STORYBOOK) {
      // eslint-disable-next-line no-console
      console.log('VIQGeoFilter confirmed geo filters:', personFilters);
    }
    const serializedValue = JSON.stringify(personFilters);
    // The agent rejects a submission field above MAX_GEO_FILTER_SUBMISSION_CHARS. Catch it here so
    // the user sees why instead of the confirmation silently failing to reach the agent.
    if (serializedValue.length > MAX_GEO_FILTER_SUBMISSION_CHARS) {
      setSubmissionError(t('aiInsights.a2ui.geoFilter.selectionTooLarge'));
      setOpen(false);
      return;
    }
    setSubmissionError(undefined);
    props.setValue(serializedValue);
    setOpen(false);
  };

  return (
    <div>
      {/* Never disabled on `isValid === false`, unlike the Button renderer: this is an input whose
          own checks (e.g. `required`) are what mark it invalid, so disabling the only control that
          can set a value would leave the user stuck. Surface the error instead, like the other
          input renderers do. */}
      <Button auto disableMinWidth css={{ width: 'fit-content' }} onPress={() => setOpen(true)}>
        <CatalogText>
          {props.label ??
            (selectedCount > 0
              ? t('aiInsights.a2ui.geoFilter.buttonLabelSelected', { count: selectedCount })
              : t('aiInsights.a2ui.geoFilter.buttonLabel'))}
        </CatalogText>
      </Button>
      {selectedCount > 0 && (
        <TriggerSubtitle>{t('aiInsights.a2ui.geoFilter.selectedSubtitle', { count: selectedCount })}</TriggerSubtitle>
      )}
      {(props.validationErrors?.[0] ?? submissionError) && (
        <ErrorLine>{props.validationErrors?.[0] ?? submissionError}</ErrorLine>
      )}
      {open && (
        // No fallback: the trigger stays in place while the chunk loads, and the modal is the only
        // thing that appears once it resolves — a spinner here would flash behind nothing.
        <Suspense fallback={null}>
          <GeoFilterModal
            open={open}
            initialGeoFilters={parseInitialGeoFilters(props.value)}
            onConfirm={handleConfirm}
            onCancel={() => setOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default VisitiqGeoFilter;
